import React, {Component} from 'react';
import {compose} from 'redux';
import PropTypes from 'prop-types';
import withFetchPreviews, {defaultVariables} from './with-fetch-previews';
import withCreateNewTripBoard, {
    addTripBoardToCache,
    optimisticNewTripBoardResponse
} from './with-create-new-trip-board';
import withAddToTripBoard, {addListingToCache, handleOnMutationError} from './with-add-to-trip-board';
import withUpdateStayOnTripBoard from './with-update-stay-on-trip-board';
import withHav from './with-hav';
import withRemoveFromTripBoards, {removeListingFromCache} from './with-remove-from-trip-boards';
import {transformTripboardData} from '../../graphql';
import TripBoardButton from '../TripBoardButton/TripBoardButton';

export class TripBoardButtonDataProvider extends Component {
    static propTypes = {
        hav: PropTypes.string.isRequired,
        pageSize: PropTypes.number,
        listingPageSize: PropTypes.number,
        fetchTripboardsQueryConfig: PropTypes.object // eslint-disable-line react/forbid-prop-types
    };

    static defaultProps = {
        pageSize: defaultVariables.pageSize,
        listingPageSize: defaultVariables.listingPageSize
    };

    constructor(props) {
        super(props);
        this.handleCreateTripBoard = this.handleCreateTripBoard.bind(this);
        this.handleAddToTripBoard = this.handleAddToTripBoard.bind(this);
        this.handleGetTripBoards = this.handleGetTripBoards.bind(this);
        this.handleRemoveFromTripBoard = this.handleRemoveFromTripBoard.bind(this);
    }

    getStay(adultCount = 2, childrenCount = 0, petsIncluded = false, startDate, endDate) {
        let stay = {adultCount, childrenCount, petsIncluded};

        if (startDate && endDate) {
            stay = stay ? stay : {};
            stay.dateRange = {arrival: startDate, departure: endDate};
        }
        return stay;
    }

    handleCreateTripBoard({listings, name, startDate, endDate, adultsCount, petIncluded, childrenCount, thumbnailUrl}) {
        const {createTripBoard, hav, pageSize, listingPageSize} = this.props;
        const listingRefs = listings.reduce((listingIds, {listingId}) => [...listingIds, listingId], []);

        const stay = this.getStay(adultsCount, childrenCount, petIncluded, startDate, endDate);
        let tripBoard = {name, listings};
        const {dateRange, ...guestContext} = stay || {};
        if (dateRange) {
            tripBoard = {...tripBoard, startDate, endDate};
        }
        tripBoard = {...tripBoard, ...guestContext};

        let mutationVariables = {
            hav,
            tripBoard
        };
        createTripBoard({
            variables: mutationVariables,
            update: addTripBoardToCache(hav, pageSize, listingPageSize),
            optimisticResponse: optimisticNewTripBoardResponse(name, listingRefs, stay, thumbnailUrl)
        });
    }

    handleAddToTripBoard({
        listingId,
        boardId,
        adultsCount,
        childrenCount,
        petIncluded,
        startDate,
        endDate,
        thumbnailUrl
    }) {
        const {addToTripBoard, updateStayOnTripBoard, hav, pageSize, listingPageSize} = this.props;
        addToTripBoard({
            variables: {
                hav,
                tripBoardUuid: boardId,
                unitTriad: listingId
            },
            onError: handleOnMutationError(listingId, boardId),
            update: addListingToCache(hav, pageSize, listingPageSize, listingId, boardId, thumbnailUrl)
        });

        const stay = this.getStay(adultsCount, childrenCount, petIncluded, startDate, endDate);

        if (stay) {
            updateStayOnTripBoard({
                variables: {
                    hav,
                    tripBoardUuid: boardId,
                    stay
                }
            });
        }
    }

    handleGetTripBoards() {
        const {tripboardData} = this.props;
        const refetch = tripboardData && tripboardData.refetch;
        if (refetch) {
            refetch();
        }
    }

    handleRemoveFromTripBoard({listingId}) {
        const {hav, removeFromTripBoards, pageSize, listingPageSize} = this.props;
        removeFromTripBoards({
            variables: {
                hav,
                unitTriad: listingId
            },
            update: removeListingFromCache(listingId, hav, pageSize, listingPageSize)
        });
    }

    render() {
        const {
            tripboardData,
            createTripBoard,
            addToTripBoard,
            updateStayOnTripBoard,
            removeFromTripBoards,
            pageSize,
            listingPageSize,
            ...childProps
        } = this.props;
        const {paginatedTripBoards, loading} = tripboardData || {};
        const transformedData = transformTripboardData(paginatedTripBoards);
        return (
            <TripBoardButton
                loading={loading}
                addToBoard={this.handleAddToTripBoard}
                createBoard={this.handleCreateTripBoard}
                removeFromBoard={this.handleRemoveFromTripBoard}
                getTripboards={this.handleGetTripBoards}
                tripboards={transformedData}
                {...childProps}
            />
        );
    }
}

export default compose(
    withHav,
    withFetchPreviews,
    withCreateNewTripBoard,
    withAddToTripBoard,
    withUpdateStayOnTripBoard,
    withRemoveFromTripBoards
)(TripBoardButtonDataProvider);
